import api from './api';

export type UserProfile = {
  name: string;
  email: string;
  role: 'student' | 'admin';
};

export type RawRole = string | number | null | undefined;

function normalizeRole(role: RawRole): UserProfile['role'] {
  if (role === undefined || role === null) {
    return 'student';
  }

  const normalized = String(role).trim().toLowerCase();

  if (normalized === 'admin' || normalized === 'administrator' || normalized === '2' || normalized === '1') {
    return 'admin';
  }

  return 'student';
}

type ProfileLike = {
  name: string;
  email: string;
  role?: RawRole;
  role_id?: RawRole;
  roleId?: RawRole;
};

function resolveRoleValue(data: ProfileLike): RawRole {
  if ('role' in data && data.role !== undefined) {
    return data.role;
  }

  if ('role_id' in data && data.role_id !== undefined) {
    return data.role_id;
  }

  if ('roleId' in data && data.roleId !== undefined) {
    return data.roleId;
  }

  return undefined;
}

export function buildProfile(data: ProfileLike): UserProfile {
  return {
    name: data.name,
    email: data.email,
    role: normalizeRole(resolveRoleValue(data)),
  };
}

const STORAGE_KEY = 'profileFallback';

export function getCachedProfile(): UserProfile | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ProfileLike;
    return buildProfile(parsed);
  } catch (error) {
    return null;
  }
}

function setLocalProfile(profile: UserProfile) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(profile));
  } catch (error) {
    // ignore storage errors
  }
}

export function cacheProfile(profile: UserProfile | null) {
  if (!profile) {
    if (typeof window !== 'undefined') {
      try {
        window.localStorage.removeItem(STORAGE_KEY);
      } catch (error) {}
    }
    return;
  }
  setLocalProfile(profile);
}

export async function getProfile(): Promise<UserProfile> {
  const { data } = await api.get('auth/me');
  if (data?.name && data?.email) {
    const profile = buildProfile(data);
    setLocalProfile(profile);
    return profile;
  }

  const fallback = getCachedProfile();
  if (fallback) return fallback;

  throw new Error('invalid-profile');
}

export async function updateProfileName(name: string): Promise<{ profile: UserProfile; persisted: boolean }>
{
  try {
    const { data } = await api.put('auth/profile', { name });
    if (data?.name && data?.email) {
      const profile = buildProfile(data);
      cacheProfile(profile);
      return { profile, persisted: true };
    }
    throw new Error('invalid-response');
  } catch (error) {
    const current = getCachedProfile();
    if (!current) {
      throw error;
    }
    const profile: UserProfile = { ...current, name };
    cacheProfile(profile);
    return { profile, persisted: false };
  }
}

export type FamilyPerformanceMetric = {
  familySlug: string;
  familyName: string;
  totalSimulations: number;
  totalQuestions: number;
  questionPool: number | null;
  correctAnswers: number;
  incorrectAnswers: number;
  accuracy: number;
  lastActivityAt: string | null;
};

export async function getFamilyPerformance(): Promise<FamilyPerformanceMetric[]> {
  const { data } = await api.get('profile/family-performance');
  if (!Array.isArray(data)) {
    return [];
  }

  return data.map((item: any) => ({
    familySlug: item.family_slug ?? item.slug ?? '',
    familyName: item.family_name ?? item.name ?? '',
    totalSimulations: Number.isFinite(item.total_simulations) ? Number(item.total_simulations) : 0,
    totalQuestions: Number.isFinite(item.total_questions) ? Number(item.total_questions) : 0,
    questionPool: Number.isFinite(item.question_pool) ? Number(item.question_pool) : null,
    correctAnswers: Number.isFinite(item.correct_answers) ? Number(item.correct_answers) : 0,
    incorrectAnswers: Number.isFinite(item.incorrect_answers) ? Number(item.incorrect_answers) : 0,
    accuracy: Number.isFinite(item.accuracy) ? Number(item.accuracy) : 0,
    lastActivityAt: typeof item.last_activity_at === 'string' ? item.last_activity_at : null,
  }));
}

export type AdaptiveWeakness = {
  categoryId: number;
  familySlug: string;
  familyName: string;
  totalSimulations: number;
  totalQuestions: number;
  correctAnswers: number;
  wrongAnswers: number;
  accuracy: number;
  avgTimePerQuestion: number;
  recommendedSize: number;
};

export type AdaptiveRecommendation = {
  categoryId: number;
  familySlug: string;
  familyName: string;
  reason: string;
  avgTimePerQuestion: number;
  recommendedSize: number;
} | null;

export type AdaptiveInsightsPayload = {
  generatedAt: string | null;
  weaknesses: AdaptiveWeakness[];
  recommendation: AdaptiveRecommendation;
};

export async function getAdaptiveInsights(): Promise<AdaptiveInsightsPayload> {
  const { data } = await api.get('profile/adaptive-insights');

  const weaknessesRaw = Array.isArray(data?.weaknesses) ? data.weaknesses : [];
  const weaknesses = weaknessesRaw.map((item: any) => ({
    categoryId: Number.isFinite(item?.category_id) ? Number(item.category_id) : 0,
    familySlug: typeof item?.family_slug === 'string' ? item.family_slug : '',
    familyName: typeof item?.family_name === 'string' ? item.family_name : '',
    totalSimulations: Number.isFinite(item?.total_simulations) ? Number(item.total_simulations) : 0,
    totalQuestions: Number.isFinite(item?.total_questions) ? Number(item.total_questions) : 0,
    correctAnswers: Number.isFinite(item?.correct_answers) ? Number(item.correct_answers) : 0,
    wrongAnswers: Number.isFinite(item?.wrong_answers) ? Number(item.wrong_answers) : 0,
    accuracy: Number.isFinite(item?.accuracy) ? Number(item.accuracy) : 0,
    avgTimePerQuestion: Number.isFinite(item?.avg_time_per_question)
      ? Number(item.avg_time_per_question)
      : 0,
    recommendedSize: Number.isFinite(item?.recommended_size) ? Number(item.recommended_size) : 20,
  }));

  const recommendationRaw = data?.recommendation;
  const recommendation: AdaptiveRecommendation = recommendationRaw && typeof recommendationRaw === 'object'
    ? {
      categoryId: Number.isFinite(recommendationRaw?.category_id) ? Number(recommendationRaw.category_id) : 0,
      familySlug: typeof recommendationRaw?.family_slug === 'string' ? recommendationRaw.family_slug : '',
      familyName: typeof recommendationRaw?.family_name === 'string' ? recommendationRaw.family_name : '',
      reason: typeof recommendationRaw?.reason === 'string'
        ? recommendationRaw.reason
        : 'Menor acurácia histórica.',
      avgTimePerQuestion: Number.isFinite(recommendationRaw?.avg_time_per_question)
        ? Number(recommendationRaw.avg_time_per_question)
        : 0,
      recommendedSize: Number.isFinite(recommendationRaw?.recommended_size)
        ? Number(recommendationRaw.recommended_size)
        : 20,
    }
    : null;

  return {
    generatedAt: typeof data?.generated_at === 'string' ? data.generated_at : null,
    weaknesses,
    recommendation,
  };
}

export type AdaptiveReviewTopic = {
  topicKey: string;
  topicSlug: string;
  topicName: string;
  attempts: number;
  correctAnswers: number;
  wrongAnswers: number;
  accuracy: number;
  avgTimeSeconds: number;
  weaknessScore: number;
  suggestedMix: {
    reviewRatio: number;
    newRatio: number;
  };
};

export type AdaptiveReviewRecommendation = {
  primaryTopicKey: string;
  primaryTopicName: string;
  reason: string;
  nextActions: string[];
  suggestedSimulation: {
    size: number;
    reviewRatio: number;
    newRatio: number;
  };
} | null;

export type AdaptiveReviewPlanPayload = {
  generatedAt: string | null;
  targetCategory: {
    categoryId: number;
    familySlug: string;
    familyName: string;
    source: string;
  } | null;
  topics: AdaptiveReviewTopic[];
  recommendation: AdaptiveReviewRecommendation;
};

export type AdaptiveReviewPlanRequest = {
  categoryId?: number;
  categorySlug?: string;
  topicsLimit?: number;
};

export async function getAdaptiveReviewPlan(
  payload: AdaptiveReviewPlanRequest = {},
): Promise<AdaptiveReviewPlanPayload> {
  const body: Record<string, unknown> = {};

  if (typeof payload.categoryId === 'number' && Number.isFinite(payload.categoryId)) {
    body.category_id = payload.categoryId;
  } else if (typeof payload.categorySlug === 'string' && payload.categorySlug.trim().length > 0) {
    body.category_slug = payload.categorySlug.trim();
  }

  if (typeof payload.topicsLimit === 'number' && Number.isFinite(payload.topicsLimit)) {
    body.topics_limit = payload.topicsLimit;
  }

  const { data } = await api.post('adaptive/review-plan', body);

  const targetRaw = data?.target_category;
  const targetCategory =
    targetRaw && typeof targetRaw === 'object'
      ? {
        categoryId: Number.isFinite(targetRaw?.category_id) ? Number(targetRaw.category_id) : 0,
        familySlug: typeof targetRaw?.family_slug === 'string' ? targetRaw.family_slug : '',
        familyName: typeof targetRaw?.family_name === 'string' ? targetRaw.family_name : '',
        source: typeof targetRaw?.source === 'string' ? targetRaw.source : 'history',
      }
      : null;

  const topicsRaw = Array.isArray(data?.topics) ? data.topics : [];
  const topics: AdaptiveReviewTopic[] = topicsRaw.map((item: any) => ({
    topicKey: typeof item?.topic_key === 'string' ? item.topic_key : '',
    topicSlug: typeof item?.topic_slug === 'string' ? item.topic_slug : '',
    topicName: typeof item?.topic_name === 'string' ? item.topic_name : 'Tópico',
    attempts: Number.isFinite(item?.attempts) ? Number(item.attempts) : 0,
    correctAnswers: Number.isFinite(item?.correct_answers) ? Number(item.correct_answers) : 0,
    wrongAnswers: Number.isFinite(item?.wrong_answers) ? Number(item.wrong_answers) : 0,
    accuracy: Number.isFinite(item?.accuracy) ? Number(item.accuracy) : 0,
    avgTimeSeconds: Number.isFinite(item?.avg_time_seconds) ? Number(item.avg_time_seconds) : 0,
    weaknessScore: Number.isFinite(item?.weakness_score) ? Number(item.weakness_score) : 0,
    suggestedMix: {
      reviewRatio: Number.isFinite(item?.suggested_mix?.review_ratio)
        ? Number(item.suggested_mix.review_ratio)
        : 70,
      newRatio: Number.isFinite(item?.suggested_mix?.new_ratio)
        ? Number(item.suggested_mix.new_ratio)
        : 30,
    },
  }));

  const recommendationRaw = data?.recommendation;
  const recommendation: AdaptiveReviewRecommendation =
    recommendationRaw && typeof recommendationRaw === 'object'
      ? {
        primaryTopicKey: typeof recommendationRaw?.primary_topic_key === 'string'
          ? recommendationRaw.primary_topic_key
          : '',
        primaryTopicName: typeof recommendationRaw?.primary_topic_name === 'string'
          ? recommendationRaw.primary_topic_name
          : 'Tópico principal',
        reason: typeof recommendationRaw?.reason === 'string'
          ? recommendationRaw.reason
          : 'Maior score de fraqueza.',
        nextActions: Array.isArray(recommendationRaw?.next_actions)
          ? recommendationRaw.next_actions.filter((item: unknown): item is string => typeof item === 'string')
          : [],
        suggestedSimulation: {
          size: Number.isFinite(recommendationRaw?.suggested_simulation?.size)
            ? Number(recommendationRaw.suggested_simulation.size)
            : 20,
          reviewRatio: Number.isFinite(recommendationRaw?.suggested_simulation?.review_ratio)
            ? Number(recommendationRaw.suggested_simulation.review_ratio)
            : 70,
          newRatio: Number.isFinite(recommendationRaw?.suggested_simulation?.new_ratio)
            ? Number(recommendationRaw.suggested_simulation.new_ratio)
            : 30,
        },
      }
      : null;

  return {
    generatedAt: typeof data?.generated_at === 'string' ? data.generated_at : null,
    targetCategory,
    topics,
    recommendation,
  };
}
